import type { JSX } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import { useAuth } from '../auth/context.js';
import { useI18n } from '../i18n/context.js';
import { AccountMenu } from '../components/AccountMenu.js';
import { LegalLinks } from '../components/LegalLinks.js';
import { BrandLogo } from '../components/BrandLogo.js';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner.js';

export function AppLayout(): JSX.Element {
  const { user } = useAuth();
  const { t } = useI18n();

  return (
    <div className="min-h-screen">
      {/* The header's content sits in the page column, so the logo lines up with what is below it. */}
      <header className="border-b border-[var(--kitup-color-muted)]/20 px-4 py-3">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <Link to="/app" aria-label={t.app.name} className="inline-flex no-underline">
            <BrandLogo />
          </Link>

          {/*
            The library sections are the product's places, not account settings: on a wide
            screen they sit in the header. On a phone they stay in the account menu.
          */}
          {user ? (
            <nav aria-label={t.app.sections} className="mr-auto hidden gap-1 text-sm md:flex">
              {(
                [
                  ['/app', t.books.title],
                  ['/app/characters', t.characters.title],
                  ['/app/things', t.things.title],
                  ['/app/locations', t.locations.title],
                ] as const
              ).map(([to, label]) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/app'}
                  className={({ isActive }) =>
                    `rounded-[var(--kitup-radius-sm)] px-3 py-1.5 font-medium no-underline ${
                      isActive
                        ? 'bg-[var(--kitup-color-accent)]/10 text-[var(--kitup-color-ink)]'
                        : 'text-[var(--kitup-color-muted)] hover:text-[var(--kitup-color-ink)]'
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </nav>
          ) : null}

          {user ? <AccountMenu name={user.name} isAdmin={user.role === 'admin'} /> : null}
        </div>
      </header>

      {user && !user.emailVerified ? <EmailVerificationBanner email={user.email} /> : null}

      <main className="px-4 py-6">
        <Outlet />
      </main>
      <footer className="border-t border-[var(--kitup-color-muted)]/20 px-4 py-5 text-sm text-[var(--kitup-color-muted)]">
        <LegalLinks compact />
      </footer>
    </div>
  );
}
